import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Bot, CheckCheck, Loader2, MessageCircle, Mic, Plus, RefreshCw, Send, Settings2, Sparkles, UserCheck, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGE_STYLES = {
  new: "bg-gray-100 text-gray-700",
  ask_treatment: "bg-[#D4AF37]/15 text-[#8a6d1f]",
  ask_budget: "bg-blue-50 text-blue-700",
  ask_location: "bg-indigo-50 text-indigo-700",
  ask_date: "bg-purple-50 text-purple-700",
  ask_slot: "bg-amber-50 text-amber-700",
  booked: "bg-green-100 text-green-800",
  done: "bg-gray-200 text-gray-600",
};

const KIND_BADGE = { bot: "Bot", ai: "AI", human: "You", followup: "Auto follow-up", failed: "Not delivered" };

// WhatsApp command centre: live bot conversations, human takeover, provider settings.
export const WhatsAppTab = ({ isAdmin }) => {
  const [stats, setStats] = useState(null);
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newLead, setNewLead] = useState({ name: "", phone: "", treatment: "", ad_campaign: "" });
  const [users, setUsers] = useState([]);
  const [channel, setChannel] = useState("whatsapp");
  const [webChats, setWebChats] = useState([]);
  const [activeWeb, setActiveWeb] = useState(null);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [s, c, w] = await Promise.all([
        api.get("/admin/whatsapp/stats"),
        api.get("/admin/conversations"),
        api.get("/admin/web-chats"),
      ]);
      setStats(s.data);
      setConvos(c.data);
      setWebChats(w.data);
      setActive((cur) => {
        if (!cur) return cur;
        const stillThere = c.data.find((x) => x.id === cur.id);
        return stillThere ? cur : cur;
      });
    } catch {
      /* quiet */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000); // live inbox
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/whatsapp/settings").then(({ data }) => setSettings(data)).catch(() => {});
      api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [active?.messages?.length]);

  const openConvo = async (id) => {
    const { data } = await api.get(`/admin/conversations/${id}`);
    setActive(data);
    setConvos((cs) => cs.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
  };

  const sendReply = async () => {
    if (!draft.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/conversations/${active.id}/send`, { text: draft.trim() });
      if (data.note) toast.warning(data.note);
      setDraft("");
      openConvo(active.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const aiSuggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/admin/conversations/${active.id}/ai-suggest`, {});
      setDraft(data.suggestion);
      toast.success("AI drafted a human-style reply — edit and send");
    } catch {
      toast.error("AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const startManual = async () => {
    try {
      const { data } = await api.post("/admin/conversations/start", newLead);
      if (data.note) toast.warning(data.note);
      else toast.success("Conversation opened — welcome WhatsApp sent");
      setShowNew(false);
      setNewLead({ name: "", phone: "", treatment: "", ad_campaign: "" });
      load();
      openConvo(data.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not start conversation");
    }
  };

  const saveWaSettings = async (patch, note) => {
    try {
      const { data } = await api.put("/admin/whatsapp/settings", patch);
      setSettings(data);
      if (note) toast.success(note);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
  };

  const [waForm, setWaForm] = useState({ phone_id: "", token: "", page_token: "", gateway: "", greeting_style: "concierge", greeting_text: "", assistant_name: "Aisha" });

  const openSettings = () => {
    setWaForm({
      phone_id: settings?.meta_phone_number_id || "",
      token: "",
      page_token: "",
      gateway: settings?.wa_gateway_url || "",
      greeting_style: settings?.greeting_style || "concierge",
      greeting_text: settings?.greeting_text || "",
      assistant_name: settings?.assistant_name || "Aisha",
    });
    setShowSettings(true);
  };

  const saveGreeting = async () => {
    if (!settings) return;
    try {
      const { data } = await api.put("/admin/whatsapp/settings", {
        wa_greeting_style: waForm.greeting_style,
        wa_greeting_text: waForm.greeting_text,
        wa_assistant_name: (waForm.assistant_name || "").trim() || "Aisha",
      });
      setSettings(data);
      toast.success(`Saved — the bot now introduces itself as ${data.assistant_name || "Aisha"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
  };

  const saveConnection = async () => {
    if (!settings) return; // settings still loading — never save blind
    const patch = {};
    const phoneId = waForm.phone_id.trim();
    if (phoneId && phoneId !== (settings.meta_phone_number_id || "")) patch.meta_phone_number_id = phoneId;
    if (waForm.token.trim()) patch.meta_access_token = waForm.token.trim();
    if (waForm.page_token.trim()) patch.meta_page_access_token = waForm.page_token.trim();
    const gateway = waForm.gateway.trim();
    if (gateway && gateway !== (settings.wa_gateway_url || "")) patch.wa_gateway_url = gateway;
    if (!Object.keys(patch).length) {
      toast.info("Nothing changed to save");
      return;
    }
    try {
      const { data } = await api.put("/admin/whatsapp/settings", patch);
      setSettings(data);
      if (data.meta_check) {
        if (data.meta_check.ok) toast.success(data.meta_check.message, { duration: 6000 });
        else toast.error(data.meta_check.message, { duration: 8000 });
      } else {
        toast.success("Connection saved");
      }
      setWaForm((f) => ({ ...f, token: "", page_token: "" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    }
  };

  const [testPhone, setTestPhone] = useState("");
  const [testSending, setTestSending] = useState(false);
  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error("Enter your WhatsApp number first");
      return;
    }
    setTestSending(true);
    try {
      const { data } = await api.post("/admin/whatsapp/test", { phone: testPhone.trim() });
      if (data.sent) toast.success("Test message sent — check your WhatsApp");
      else toast.error(data.note || "Send failed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Test failed");
    } finally {
      setTestSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="whatsapp-tab">
      {/* slim command bar — stats + actions in one row, no page scroll */}
      <div className="glass flex flex-wrap items-center gap-x-7 gap-y-3 rounded-2xl px-5 py-3.5" data-testid="wa-command-bar">
        {[
          ["Conversations", stats?.total ?? "—"],
          ["Unread", stats?.unread ?? "—"],
          ["Booked via bot", stats?.booked ?? "—"],
          ["Booking rate", stats ? `${stats.booking_rate}%` : "—"],
          ["Learned patterns", stats?.learned_patterns ?? "—"],
        ].map(([label, val]) => (
          <div key={label} className="flex items-baseline gap-2" data-testid={`wa-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="font-serif text-xl font-semibold text-[#1A1615]">{val}</span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-[#91837C]">{label}</span>
          </div>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button onClick={() => setShowNew(true)} data-testid="wa-new-conversation" className="btn-gold inline-flex items-center gap-2 px-4 py-2 text-[10px]">
            <Plus className="h-3.5 w-3.5" /> Add Lead & Message
          </button>
          <button onClick={load} data-testid="wa-refresh" className="btn-outline-lux inline-flex items-center gap-2 px-3.5 py-2 text-[10px]" aria-label="Refresh inbox">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          {isAdmin && (
            <button onClick={openSettings} data-testid="wa-settings-toggle" className="btn-outline-lux inline-flex items-center gap-2 px-3.5 py-2 text-[10px]">
              <Settings2 className="h-3.5 w-3.5" /> Engine
            </button>
          )}
          {settings && (
            <span className="rounded-full bg-green-50 px-3 py-1.5 text-[10px] font-semibold text-green-800" data-testid="wa-provider-badge">
              {settings.provider} · meta {settings.meta_ready ? "ready" : "not set"} · twilio {settings.twilio_ready ? "ready" : "not set"} · gateway {settings.gateway_ready ? "ready" : "not set"}
            </span>
          )}
        </div>
      </div>

      {/* provider settings (admin) — popup dialog, keeps the inbox clean */}
      {isAdmin && showSettings && settings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="wa-settings" role="dialog" aria-modal="true" aria-label="WhatsApp engine settings">
          <div className="absolute inset-0 bg-[#1A1615]/40 backdrop-blur-sm" onClick={() => setShowSettings(false)} />
          <div className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg font-semibold text-[#1A1615]">WhatsApp Engine</h3>
              <button onClick={() => setShowSettings(false)} data-testid="wa-settings-close" className="text-[#91837C] hover:text-[#1A1615]" aria-label="Close settings"><X className="h-5 w-5" /></button>
            </div>
          <p className="mt-1 text-xs font-light text-[#91837C]">
            Official Meta Cloud API is recommended (free tier, no ban risk). Twilio is paid but already familiar. Gateway = your own open-source Baileys server (free but unofficial — ban risk is yours).
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {["meta", "twilio", "gateway"].map((p) => (
              <button
                key={p}
                onClick={() => saveWaSettings({ whatsapp_provider: p }, `Engine switched to ${p}`)}
                data-testid={`wa-provider-${p}`}
                className={`rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${settings.provider === p ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}
              >
                {p === "meta" ? "Meta Cloud API (official)" : p === "twilio" ? "Twilio" : "Open gateway (self-hosted)"}
              </button>
            ))}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-[#5A504B]">Meta Phone Number ID
              <input className="input-lux mt-1 w-full text-xs" placeholder="From API Setup page" value={waForm.phone_id} onChange={(e) => setWaForm({ ...waForm, phone_id: e.target.value })} data-testid="wa-meta-phone-id" />
            </label>
            <label className="text-xs text-[#5A504B]">Meta access token {settings.has_meta_token && <span className="font-semibold text-emerald-700">(saved ✓)</span>}
              <input type="password" className="input-lux mt-1 w-full text-xs" placeholder={settings.has_meta_token ? "Saved — paste new token to replace" : "System-user token"} value={waForm.token} onChange={(e) => setWaForm({ ...waForm, token: e.target.value })} data-testid="wa-meta-token" />
            </label>
            <label className="text-xs text-[#5A504B]">Meta Page token (for FB/IG ad leads) {settings.has_page_token && <span className="font-semibold text-emerald-700">(saved ✓)</span>}
              <input type="password" className="input-lux mt-1 w-full text-xs" placeholder="Page access token with leads_retrieval" value={waForm.page_token} onChange={(e) => setWaForm({ ...waForm, page_token: e.target.value })} data-testid="wa-page-token" />
            </label>
            <label className="text-xs text-[#5A504B]">Gateway URL (open-source server)
              <input className="input-lux mt-1 w-full text-xs" placeholder="https://your-baileys-server.com" value={waForm.gateway} onChange={(e) => setWaForm({ ...waForm, gateway: e.target.value })} data-testid="wa-gateway-url" />
            </label>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button onClick={saveConnection} disabled={!settings} data-testid="wa-settings-save" className="btn-gold px-6 py-2.5 text-[10px] disabled:opacity-60">Save Connection</button>
            <span className={`text-[11px] font-medium ${settings.meta_ready ? "text-emerald-700" : "text-amber-600"}`} data-testid="wa-meta-ready-hint">
              {settings.meta_ready ? "Meta is connected — the bot can send" : "Meta needs BOTH the Phone Number ID and the access token to send"}
            </span>
          </div>
          <div className="mt-5 rounded-xl border border-[#D4AF37]/25 bg-[#FAF8F5] p-4" data-testid="wa-greeting-section">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a6d1f]">Bot identity &amp; greeting</p>
            <label className="mt-2 block text-[11px] font-medium text-[#5A504B]">
              Assistant name — how the bot introduces itself when a guest asks &ldquo;who is this?&rdquo;
              <input
                className="input-lux mt-1 w-full text-xs"
                placeholder="e.g. Aisha"
                value={waForm.assistant_name}
                onChange={(e) => setWaForm({ ...waForm, assistant_name: e.target.value })}
                data-testid="wa-assistant-name"
              />
            </label>
            <p className="mt-3 text-[11px] font-light text-[#91837C]">
              How the bot greets first-time WhatsApp visitors. Pick a signature style, or write your own template below
              (placeholders: {"{name}"}, {"{clinic}"}, {"{treatment}"}). Guests writing in Urdu/Hindi receive it in their language automatically.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {[["concierge", "Warm Concierge"], ["physician", "Physician-Led"], ["minimal", "Minimal Luxe"]].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setWaForm({ ...waForm, greeting_style: k })}
                  data-testid={`wa-greeting-${k}`}
                  className={`rounded-full px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${waForm.greeting_style === k ? "bg-[#1A1615] text-[#D4AF37]" : "bg-white text-[#91837C] hover:text-[#1A1615]"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <textarea
              rows={2}
              className="input-lux mt-3 w-full resize-none text-xs"
              placeholder="Optional: your own greeting template (overrides the style) — e.g. Salaam {name}! Welcome to {clinic}. How may I help?"
              value={waForm.greeting_text}
              onChange={(e) => setWaForm({ ...waForm, greeting_text: e.target.value })}
              data-testid="wa-greeting-text"
            />
            <button onClick={saveGreeting} data-testid="wa-greeting-save" className="btn-gold mt-3 px-6 py-2 text-[10px]">Save Greeting</button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl bg-[#FAF8F5] p-3">
            <input className="input-lux flex-1 text-xs sm:max-w-56" placeholder="Your WhatsApp number (+971…)" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} data-testid="wa-test-phone" />
            <button onClick={sendTest} disabled={testSending} data-testid="wa-test-send" className="btn-outline-lux inline-flex items-center gap-1.5 px-4 py-2 text-[10px] disabled:opacity-60">
              {testSending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send Test Message
            </button>
            <span className="text-[10px] font-light text-[#91837C]">Sends a real WhatsApp from the clinic number — the surest way to confirm everything works.</span>
          </div>
          <div className="mt-4 rounded-xl bg-[#FAF8F5] p-4 text-xs text-[#5A504B]">
            <p className="font-semibold text-[#1A1615]">Webhook for Meta for Developers → Webhooks:</p>
            <p className="mt-1 font-mono text-[11px]" data-testid="wa-webhook-url">{window.location.origin}/api/webhooks/meta</p>
            <p className="mt-1 font-mono text-[11px]" data-testid="wa-verify-token">Verify token: {settings.meta_verify_token}</p>
            <p className="mt-2 font-light">Subscribe to WhatsApp <b>messages</b> and Page <b>leadgen</b> fields. First contact with a new lead needs an approved template in Meta; once a guest replies, the 24-hour window opens for free messaging.</p>
          </div>
          <label className="mt-4 flex items-center gap-2 text-xs text-[#5A504B]">
            <input type="checkbox" checked={settings.followups_enabled !== "off"} onChange={(e) => saveWaSettings({ wa_followups: e.target.checked ? "on" : "off" }, "Follow-ups " + (e.target.checked ? "on" : "paused"))} data-testid="wa-followups-toggle" className="accent-[#B38B2D]" />
            Automatic follow-ups (day 1 / 3 / 7) + appointment reminders on WhatsApp &amp; email
          </label>
          </div>
        </div>
      )}

      {/* inbox — fills the screen; only the message areas scroll */}
      <div className="glass grid gap-0 rounded-2xl lg:h-[calc(100vh-12.5rem)] lg:grid-cols-[340px_1fr]" data-testid="wa-inbox">
        <div className={`max-h-[70vh] overflow-y-auto border-b border-[#EFE7DF] lg:border-b-0 lg:border-r ${(channel === "whatsapp" ? active : activeWeb) ? "hidden lg:block" : ""} lg:max-h-none lg:h-full`}>
          <div className="sticky top-0 z-10 flex gap-1 border-b border-[#EFE7DF] bg-white/90 p-2 backdrop-blur">
            {[["whatsapp", "WhatsApp"], ["web", "Website chats"]].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setChannel(id)}
                data-testid={`inbox-channel-${id}`}
                className={`flex-1 rounded-lg px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] transition-colors ${channel === id ? "bg-[#1A1615] text-[#D4AF37]" : "text-[#91837C] hover:text-[#1A1615]"}`}
              >
                {label}{id === "web" && webChats.length ? ` (${webChats.length})` : ""}
              </button>
            ))}
          </div>
          {channel === "web" && (
            <>
              {webChats.map((w) => (
                <button
                  key={w.session_id}
                  onClick={async () => {
                    const { data } = await api.get(`/admin/web-chats/${w.session_id}`);
                    setActiveWeb(data);
                  }}
                  data-testid={`web-chat-${w.session_id}`}
                  className={`flex w-full items-start gap-3 border-b border-[#F5EEE7] px-4 py-3 text-left transition-colors hover:bg-white/70 ${activeWeb?.session_id === w.session_id ? "bg-white" : ""}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#1A1615] text-[#D4AF37]">
                    <MessageCircle className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-semibold text-[#1A1615]">{w.first_message || "Website visitor"}</span>
                      {w.lead_captured && <span className="rounded-full bg-green-100 px-1.5 py-0.5 text-[8px] font-bold uppercase text-green-800">lead</span>}
                    </span>
                    <span className="block truncate text-[11px] font-light text-[#91837C]">{w.last_message}</span>
                    <span className="mt-1 text-[9px] text-[#C9B8A8]">{w.message_count} messages · web chat</span>
                  </span>
                </button>
              ))}
              {!webChats.length && <p className="p-6 text-center text-xs font-light text-[#91837C]">No website chats yet — Aisha is standing by on the site.</p>}
            </>
          )}
          {channel === "whatsapp" && convos.map((c) => (
            <button
              key={c.id}
              onClick={() => openConvo(c.id)}
              data-testid={`wa-convo-${c.id}`}
              className={`flex w-full items-start gap-3 border-b border-[#F5EEE7] px-4 py-3 text-left transition-colors hover:bg-white/70 ${active?.id === c.id ? "bg-white" : ""}`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F5EBE6] text-[#8a6d1f]">
                <MessageCircle className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs font-semibold text-[#1A1615]">{c.name || c.phone}</span>
                  {c.unread > 0 && <span className="rounded-full bg-[#B38B2D] px-1.5 py-0.5 text-[9px] font-bold text-white">{c.unread}</span>}
                </span>
                <span className="block truncate text-[11px] font-light text-[#91837C]">{c.last_message || "…"}</span>
                <span className="mt-1 flex items-center gap-1.5">
                  <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] ${STAGE_STYLES[c.stage] || STAGE_STYLES.new}`}>{(c.stage || "new").replace(/_/g, " ")}</span>
                  <span className="text-[9px] text-[#C9B8A8]">{c.source}</span>
                </span>
              </span>
            </button>
          ))}
          {channel === "whatsapp" && !convos.length && <p className="p-6 text-center text-xs font-light text-[#91837C]">No conversations yet — add a lead or connect the webhook.</p>}
        </div>

        {/* chat pane */}
        <div className={`flex-col lg:h-full ${(channel === "whatsapp" ? active : activeWeb) ? "flex h-[70vh]" : "hidden lg:flex"}`}>
          {channel === "web" ? (
            activeWeb ? (
              <>
                <div className="flex flex-wrap items-center gap-2 border-b border-[#EFE7DF] px-4 py-3">
                  <button onClick={() => setActiveWeb(null)} data-testid="web-chat-back" className="inline-flex items-center gap-1 rounded-full bg-[#F5EBE6] px-3 py-1.5 text-[10px] font-semibold text-[#5A504B] lg:hidden" aria-label="Back to chat list">
                    <ArrowLeft className="h-3 w-3" /> Chats
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-[#1A1615]" data-testid="web-active-title">Website visitor · {activeWeb.session_id.slice(0, 10)}</p>
                    <p className="text-[10px] font-light text-[#91837C]">
                      {activeWeb.lead_captured ? "Phone captured — lead created" : "Browsing the site"} · {(activeWeb.messages || []).length} messages
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const { data } = await api.get(`/admin/web-chats/${activeWeb.session_id}`);
                      setActiveWeb(data);
                    }}
                    data-testid="web-chat-refresh"
                    className="btn-outline-lux inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px]"
                  >
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </button>
                </div>
                <div className="flex-1 min-h-0 space-y-2 overflow-y-auto bg-[#FAF7F3] p-4" data-testid="web-chat-messages">
                  {(activeWeb.messages || []).map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-start" : "justify-end"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-xs leading-relaxed ${m.role === "user" ? "bg-white text-[#1A1615] shadow-sm" : "bg-[#1A1615] text-[#FAF8F5]"}`}>
                        <p className="whitespace-pre-wrap">{m.text}</p>
                        <p className={`mt-1 text-[9px] ${m.role === "user" ? "text-[#C9B8A8]" : "text-[#D4AF37]/70"}`}>
                          {m.role === "user" ? "Visitor" : m.kind === "human" ? `Staff · ${m.by || ""}` : "Aisha (AI)"} · {new Date(m.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 border-t border-[#EFE7DF] p-3">
                  <input
                    className="input-lux flex-1 text-xs"
                    placeholder="Reply as the clinic — the visitor sees this in the chat widget…"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === "Enter" && draft.trim()) {
                        await api.post(`/admin/web-chats/${activeWeb.session_id}/staff-reply`, { text: draft.trim() });
                        setDraft("");
                        const { data } = await api.get(`/admin/web-chats/${activeWeb.session_id}`);
                        setActiveWeb(data);
                      }
                    }}
                    data-testid="web-staff-input"
                  />
                  <button
                    onClick={async () => {
                      if (!draft.trim()) return;
                      await api.post(`/admin/web-chats/${activeWeb.session_id}/staff-reply`, { text: draft.trim() });
                      setDraft("");
                      const { data } = await api.get(`/admin/web-chats/${activeWeb.session_id}`);
                      setActiveWeb(data);
                      toast.success("Sent — the visitor sees it in the chat widget");
                    }}
                    data-testid="web-staff-send"
                    className="btn-gold px-4 py-2.5 text-[10px]"
                  >
                    <Send className="h-3.5 w-3.5" />
                  </button>
                </div>
              </>
            ) : (
              <div className="flex flex-1 items-center justify-center p-10 text-center text-xs font-light text-[#91837C]">
                Select a website chat to read the transcript — or reply as the clinic.
              </div>
            )
          ) : active ? (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-[#EFE7DF] px-4 py-3">
                <button onClick={() => setActive(null)} data-testid="wa-chat-back" className="inline-flex items-center gap-1 rounded-full bg-[#F5EBE6] px-3 py-1.5 text-[10px] font-semibold text-[#5A504B] lg:hidden" aria-label="Back to conversation list">
                  <ArrowLeft className="h-3 w-3" /> Chats
                </button>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#1A1615]" data-testid="wa-active-name">{active.name || active.phone}</p>
                  <p className="text-[10px] font-light text-[#91837C]">
                    {[active.context?.treatment, active.context?.budget, active.context?.location, active.context?.preferred_date && `${active.context.preferred_date} ${active.context?.preferred_time || ""}`].filter(Boolean).join(" · ") || "Qualifying…"}
                  </p>
                </div>
                {isAdmin && users.length > 0 && (
                  <select
                    value={active.assigned_to || ""}
                    onChange={(e) => api.post(`/admin/conversations/${active.id}/assign`, { user_id: e.target.value }).then(() => toast.success("Assigned"))}
                    data-testid="wa-assign"
                    className="input-lux !w-auto text-[10px]"
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => <option key={u._id} value={u._id}>{u.name || u.email}</option>)}
                  </select>
                )}
                <button
                  onClick={() => api.post(`/admin/conversations/${active.id}/toggle-bot`, { enabled: !active.bot_enabled }).then(() => openConvo(active.id))}
                  data-testid="wa-bot-toggle"
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] ${active.bot_enabled ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}
                >
                  <Bot className="h-3 w-3" /> Bot {active.bot_enabled ? "on" : "off"}
                </button>
              </div>
              <div className="flex-1 min-h-0 space-y-2 overflow-y-auto bg-[#FAF7F3] p-4" data-testid="wa-messages">
                {(active.messages || []).filter((m) => m.kind !== "ai_draft").map((m, i) => (
                  <div key={i} className={`flex ${m.direction === "out" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-xs leading-relaxed ${m.direction === "out" ? "bg-[#1A1615] text-[#FAF8F5]" : "bg-white text-[#1A1615] shadow-sm"}`}>
                      {m.quoted_text && (
                        <p className={`mb-1.5 rounded-lg border-l-2 px-2 py-1 text-[10px] italic ${m.direction === "out" ? "border-[#D4AF37] bg-white/10 text-[#E8DCC8]" : "border-[#D4AF37] bg-[#F5EBE6]/60 text-[#8a6d1f]"}`} data-testid="wa-quoted-text">
                          {m.quoted_text}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap">{m.text}</p>
                      <p className={`mt-1 flex items-center gap-1 text-[9px] ${m.direction === "out" ? "text-[#D4AF37]/70" : "text-[#C9B8A8]"}`}>
                        {m.via === "voice" && (
                          <span className="inline-flex items-center gap-0.5 rounded-full bg-[#D4AF37]/15 px-1.5 py-0.5 font-semibold text-[#8a6d1f]" data-testid="wa-voice-badge">
                            <Mic className="h-2.5 w-2.5" /> voice
                          </span>
                        )}
                        {KIND_BADGE[m.kind] || m.kind} · {new Date(m.at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                      {m.kind === "failed" && m.fail_reason && (
                        <p className="mt-1 text-[9px] font-medium leading-snug text-red-300" data-testid="wa-fail-reason">
                          {m.fail_reason}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {/* brain draft awaiting staff approval */}
              {active.ai_draft_pending && (active.messages || []).some((m) => m.kind === "ai_draft") && (
                <div className="border-t border-[#D4AF37]/30 bg-[#F8F3E6] px-4 py-3" data-testid="wa-ai-draft-panel">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#8a6d1f]">Brain draft — review, edit if needed, then send</p>
                  <textarea
                    rows={3}
                    defaultValue={[...active.messages].reverse().find((m) => m.kind === "ai_draft")?.text || ""}
                    id="wa-draft-editor"
                    className="input-lux mt-1.5 w-full resize-none text-xs"
                    data-testid="wa-draft-text"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      onClick={async () => {
                        const text = document.getElementById("wa-draft-editor")?.value || "";
                        const { data } = await api.post(`/admin/conversations/${active.id}/send-draft`, { text });
                        if (data.note) toast.error(data.note);
                        else toast.success("Brain reply sent to the client");
                        openConvo(active.id);
                      }}
                      data-testid="wa-draft-send"
                      className="btn-gold inline-flex items-center gap-1.5 px-4 py-2 text-[10px]"
                    >
                      <Send className="h-3 w-3" /> Send Reply
                    </button>
                    <button
                      onClick={async () => { await api.post(`/admin/conversations/${active.id}/dismiss-draft`); openConvo(active.id); }}
                      data-testid="wa-draft-dismiss"
                      className="btn-outline-lux px-4 py-2 text-[10px]"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 border-t border-[#EFE7DF] p-3">
                <button onClick={aiSuggest} disabled={suggesting} data-testid="wa-ai-suggest" className="btn-outline-lux inline-flex items-center gap-1.5 px-3 py-2.5 text-[10px] disabled:opacity-60">
                  {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI draft
                </button>
                <input
                  className="input-lux flex-1 text-xs"
                  placeholder={active.bot_enabled ? "Bot is replying automatically — turn it off to type yourself" : "Type a human reply…"}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !sending && sendReply()}
                  data-testid="wa-reply-input"
                />
                <button onClick={sendReply} disabled={sending || !draft.trim()} data-testid="wa-send" className="btn-gold px-4 py-2.5 text-[10px] disabled:opacity-60">
                  {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-10 text-center text-xs font-light text-[#91837C]">
              Select a conversation — or add a lead to start one.
            </div>
          )}
        </div>
      </div>

      {/* new lead modal */}
      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1A1615]/50 p-4 backdrop-blur-sm" data-testid="wa-new-modal">
          <div className="glass w-full max-w-md rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl font-semibold text-[#1A1615]">Add Lead &amp; Open WhatsApp</h3>
              <button onClick={() => setShowNew(false)} data-testid="wa-new-cancel" className="text-[#91837C] hover:text-[#1A1615]" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <input className="input-lux w-full text-xs" placeholder="Name" value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })} data-testid="wa-new-name" />
              <input className="input-lux w-full text-xs" placeholder="Phone with country code, e.g. 9715xxxxxxx" value={newLead.phone} onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })} data-testid="wa-new-phone" />
              <input className="input-lux w-full text-xs" placeholder="Treatment / ad interest (mentioned in the opening message)" value={newLead.treatment} onChange={(e) => setNewLead({ ...newLead, treatment: e.target.value })} data-testid="wa-new-treatment" />
              <input className="input-lux w-full text-xs" placeholder="Ad set / campaign (optional, e.g. IG-May-HydraFacial)" value={newLead.ad_campaign} onChange={(e) => setNewLead({ ...newLead, ad_campaign: e.target.value })} data-testid="wa-new-campaign" />
            </div>
            <button onClick={startManual} data-testid="wa-new-submit" className="btn-gold mt-5 w-full py-3 text-xs">Send Opening WhatsApp</button>
          </div>
        </div>
      )}
    </div>
  );
};
